import { useMemo } from 'react';
import { getBEFinSession } from 'api/v2/users/beFinances';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { getBEFinSessionQueryKey } from 'hooks/useBEFinSession/getBEFinSessionQueryKey';
import { useClientWorkspaces } from 'hooks/Workspace/useClientWorkspaces';
import { VATTypeEnum } from 'types/users.types';
import type { IWorkspace } from 'types/workspace.types';
import useQuery from 'hooks/react-query/useQuery';
import { isBEFinFeatureEnabled } from 'utils/docsRepo/useIsBEFinFeatureEnabled/useIsBEFinFeatureEnabled';

const STALE_TIME_MS = 2 * 60 * 1000;

export type UseIntervatAccountantPermissionOptions = {
  /**
   * When true, skips paginated workspace fetches. Use when the caller already
   * knows the workspace (e.g. Data Access) and only needs BEFin session / grant path.
   */
  skipWorkspaceFetch?: boolean;
};

/**
 * InterVAT accountant permission (taxes.us.31):
 * - Whether user is eligible for the grant alert (BEFin/InterVAT country, subject to VAT, has accountant(s), none with InterVAT yet).
 * - List of accountant workspaces for the grant modal (excl. support).
 */
export const useIntervatAccountantPermission = (
  options: UseIntervatAccountantPermissionOptions = {},
): {
  eligible: boolean;
  isLoading: boolean;
  accountantWorkspaces: IWorkspace[];
  /** True when BEFin session is active and user has Intervat consent (not just MinFin). Grant Intervat to accountant without redirect. */
  canGrantIntervatWithoutRedirect: boolean;
} => {
  const { skipWorkspaceFetch = false } = options;
  const { data: user, isLoading: userLoading } = useGetCurrentCustomerData();

  const isBEFinCountry = isBEFinFeatureEnabled(user);
  const isSubjectToVat =
    isBEFinCountry && user?.VATType === VATTypeEnum.subjectToVAT;

  const workspacesEnabled = isSubjectToVat && !skipWorkspaceFetch;
  const { categorized: workspacesData, isLoading: workspacesQueryLoading } =
    useClientWorkspaces({ enabled: workspacesEnabled });

  const { data: beFinSession, isLoading: sessionLoading } = useQuery({
    queryKey: getBEFinSessionQueryKey(),
    queryFn: getBEFinSession,
    enabled: isSubjectToVat,
    staleTime: STALE_TIME_MS,
  });

  const eligible = useMemo(() => {
    if (!isSubjectToVat) return false;

    const workspacesWithAccessToClient = workspacesData?.general ?? [];
    if (workspacesWithAccessToClient.length === 0) return false;

    if (beFinSession?.intervatGrantedWorkspaceId) return false;
    const hasIntervatGranted = workspacesWithAccessToClient.some(
      (workspace) => workspace.intervatGranted === true,
    );
    if (hasIntervatGranted) return false;

    return true;
  }, [
    isSubjectToVat,
    workspacesData?.general,
    beFinSession?.intervatGrantedWorkspaceId,
  ]);

  const workspacesLoading = !skipWorkspaceFetch && workspacesQueryLoading;

  const isLoading =
    userLoading ||
    (isBEFinCountry &&
      (workspacesLoading || (isSubjectToVat && sessionLoading)));

  const accountantWorkspaces = workspacesData?.general ?? [];
  const canGrantIntervatWithoutRedirect =
    (beFinSession?.isSessionActive ?? false) &&
    (beFinSession?.consents?.intervatApi ?? false);

  return {
    eligible,
    isLoading: !!isLoading,
    accountantWorkspaces,
    canGrantIntervatWithoutRedirect,
  };
};
